import { useEffect, useState } from 'react';

// Hooks leves de animação da landing. Tudo com cleanup e respeitando
// `prefers-reduced-motion`.

export function prefersReducedMotion() {
  return typeof window !== 'undefined' && !!window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

// Revela (classe `in`) todos os `.reveal` dentro de `rootRef` quando entram na viewport.
export function useRevealOnScroll(rootRef) {
  useEffect(() => {
    const root = rootRef.current;
    if (!root) return undefined;
    const els = Array.from(root.querySelectorAll('.reveal'));
    if (typeof IntersectionObserver === 'undefined') {
      els.forEach((el) => el.classList.add('in'));
      return undefined;
    }
    const io = new IntersectionObserver(
      (entries) => entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('in');
          io.unobserve(entry.target);
        }
      }),
      { threshold: 0.15, rootMargin: '0px 0px -40px 0px' },
    );
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, [rootRef]);
}

// Dispara `true` na primeira vez que o elemento aparece na viewport.
export function useInViewOnce(ref, threshold = 0.3) {
  const [seen, setSeen] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    if (typeof IntersectionObserver === 'undefined') {
      setSeen(true);
      return undefined;
    }
    const io = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting)) {
        setSeen(true);
        io.disconnect();
      }
    }, { threshold });
    io.observe(el);
    return () => io.disconnect();
  }, [ref, threshold]);
  return seen;
}

// Número que sobe até `to` (ease-out cúbico) quando `play` fica verdadeiro; enquanto
// `play` é falso mostra `idle`. `runKey` permite reanimar (ex.: ao voltar para a aba).
export function useCountUp(to, { play = true, duration = 1400, delay = 0, runKey = 0, idle = to } = {}) {
  const [value, setValue] = useState(idle);
  useEffect(() => {
    if (!play) {
      setValue(idle);
      return undefined;
    }
    if (prefersReducedMotion()) {
      setValue(to);
      return undefined;
    }
    let raf = 0;
    setValue(0);
    const t0 = performance.now() + delay;
    const step = (now) => {
      const k = Math.min(Math.max((now - t0) / duration, 0), 1);
      setValue(to * (1 - (1 - k) ** 3));
      if (k < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [to, play, duration, delay, runKey, idle]);
  return Math.round(value);
}

// Digitação rotativa (digita, segura, apaga e passa para a próxima palavra).
export function useTyper(words, { hold = 1600, speed = 70, enabled = true } = {}) {
  const [text, setText] = useState(() => (prefersReducedMotion() ? words[0] : ''));
  useEffect(() => {
    if (!enabled) return undefined;
    if (prefersReducedMotion()) {
      setText(words[0]);
      return undefined;
    }
    let w = 0;
    let c = 0;
    let del = false;
    let timer;
    const tick = () => {
      const word = words[w];
      setText(word.slice(0, c));
      if (!del && c === word.length) {
        del = true;
        timer = setTimeout(tick, hold);
        return;
      }
      if (del && c === 0) {
        del = false;
        w = (w + 1) % words.length;
      }
      c += del ? -1 : 1;
      timer = setTimeout(tick, del ? 35 : speed);
    };
    tick();
    return () => clearTimeout(timer);
  }, [words, hold, speed, enabled]);
  return text;
}

// Spotlight que segue o cursor nos `.card-glow` (via custom properties --mx/--my).
export function useCardSpotlight(rootRef) {
  useEffect(() => {
    const root = rootRef.current;
    if (!root) return undefined;
    const onMove = (event) => {
      const card = event.target.closest && event.target.closest('.card-glow');
      if (!card) return;
      const rect = card.getBoundingClientRect();
      card.style.setProperty('--mx', `${event.clientX - rect.left}px`);
      card.style.setProperty('--my', `${event.clientY - rect.top}px`);
    };
    root.addEventListener('pointermove', onMove);
    return () => root.removeEventListener('pointermove', onMove);
  }, [rootRef]);
}
